import {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import useAlerts from '../../../hooks/use-alerts';
import useServices from '../../../hooks/use-services';
import useStore from '../../../hooks/use-store';
import {findEventGeofence, isGeofenceEvent, posicaoDoEvento} from '../../../helpers/geofenceEvent';
import {actions} from '../reducers';

/**
 * Cercas do mapa: lista, desenho, criação e o foco na cerca citada por um evento.
 *
 * As cercas ficam no estado do mapa (`state.geofences`), de onde o gancho do evento também as lê.
 *
 * @param {Object} state estado do mapa (`instance`, `geofences`)
 * @param {Function} dispatch redutor do mapa
 * @param {Object} instance não usado
 * @return {Object} `devices`, `showGeofences`, `setShowGeofences`, `createGeofenceForCurrentDevice`,
 *   `focusEventGeofence(event, onFocused)` e `clearGeofenceFocus()`
 */
const useMapGeofences = (state, dispatch, instance) => {
    const {toast} = useAlerts();
    const intl = useIntl();
    const {store} = useStore();
    const {events, devices} = store;
    const {geofenceService, permissionService, positionService} = useServices();

    const [showGeofences, setShowGeofences] = useState(true);
    const {instance: map, geofences} = state;
    const setGeofences = (list) => dispatch({type: actions.SET_GEOFENCES, payload: list});
    const focusToken = useRef(0);
    const inFlight = useRef(null);
    const loaded = useRef(false);
    if (geofences) loaded.current = true;

    /**
     * CONSTRAINT: chamadas sobrepostas (a consulta inicial e a de um foco) dividem a mesma consulta, para
     * a lista entrar no estado uma vez só e as cercas não serem desenhadas duas vezes.
     *
     * @return {Promise<Object[]|null>} a lista, já no estado do mapa; `null` se a consulta falhou, e o
     *   estado fica como estava
     */
    const fetchGeofences = () => {
        if (!inFlight.current) {
            inFlight.current = (async () => {
                try {
                    const response = await geofenceService.list();
                    if (!Array.isArray(response)) return null;
                    loaded.current = true;
                    setGeofences(response);
                    return response;
                } catch (error) {
                    return null;
                } finally {
                    inFlight.current = null;
                }
            })();
        }
        return inFlight.current;
    };

    // CONSTRAINT: a camada é esvaziada antes, porque a lista pode mudar (cerca criada, consulta nova) sem
    // que `showGeofences` passe por falso, e desenhar por cima duplicaria as cercas.
    const drawGeofences = () => {
        if (geofences) {
            map.removeGeofences();
            geofences.forEach((geofence) => {
                map.addGeofence(geofence);
            });
        }
    };

    /**
     * Create a circle geofence around selected device or events current device.
     * The geofence is created at `device.currentPosition`.
     * @return {Promise<undefined>}
     */
    const createGeofenceForCurrentDevice = async () => {
        if (!map) return;

        const device = devices.selectedDevice || events.currentDevice;
        const currentPosition = device.currentPosition;

        if (currentPosition) {
            const area = `CIRCLE (${currentPosition.latitude} ${
                currentPosition.longitude
            }, ${20})`;

            const data = {name: `Cerca_${device.name}`, area};

            const response = await geofenceService.save(data);

            if (response) {
                const permission = {deviceId: device.id, geofenceId: response.id};
                await permissionService.addPermission(permission);
                // A lista pode ter chegado, ou falhado, durante as esperas acima: decide pelo estado de agora.
                if (inFlight.current) await inFlight.current;
                if (loaded.current) {
                    dispatch({type: actions.ADD_GEOFENCE, payload: response});
                } else {
                    await fetchGeofences();
                }
                toast.success(intl.formatMessage({id: 'geofenceCreateSuccess'}));
            } else {
                toast.success(intl.formatMessage({id: 'geofenceCreateFailed'}));
            }
        }
    };

    /**
     * Leva o mapa à cerca citada por um evento de cerca, com a posição do evento; ver
     * docs/notificacao-de-cerca.md.
     *
     * BUSINESS RULE: o foco independe de `showGeofences`, que a escolha do usuário mantém como está.
     * BUSINESS RULE: lista de cercas ainda não carregada (a consulta inicial falhou ou não terminou) é
     * consultada uma vez aqui; se continua indisponível, o chamador segue com o veículo.
     * CONSTRAINT: uma seleção nova (`clearGeofenceFocus`) enquanto a lista ou a posição são consultadas
     * cancela este foco: o resultado dele não é aplicado.
     *
     * @param {Object} event evento clicado (`type`, `geofenceId`, `positionId`)
     * @param {Function} [onFocused] chamada quando o foco é aplicado ao mapa
     * @return {Promise<boolean>} `false` quando o chamador deve seguir com o veículo: o evento não é de
     *   cerca, a cerca não está na lista do usuário, a lista não veio ou a área não se lê. `true` quando o
     *   foco foi aplicado ou foi substituído por outra seleção.
     */
    const focusEventGeofence = async (event, onFocused) => {
        if (!map || !isGeofenceEvent(event)) return false;

        const token = ++focusToken.current;
        const list = geofences || await fetchGeofences();
        if (token !== focusToken.current) return true;

        const geofence = findEventGeofence(list, event);
        if (!geofence) return false;

        let position = null;
        try {
            position = await posicaoDoEvento(positionService, event.positionId);
        } catch (error) {
            position = null;
        }
        if (token !== focusToken.current) return true;

        const focused = map.focusGeofence(geofence, position ? [position.longitude, position.latitude] : null);
        if (focused && onFocused) onFocused();
        return focused;
    };

    const clearGeofenceFocus = () => {
        focusToken.current++;
        if (map) map.clearGeofenceFocus();
    };

    useEffect(() => {
        fetchGeofences();
    }, []);

    useEffect(() => () => {
        focusToken.current++;
        if (map) map.clearGeofenceFocus();
    }, [map]);

    useEffect(() => {
        if (map) {
            map.initGeofencesLayer();
        }
    }, [map]);

    useEffect(() => {
        if (map) {
            if (geofences && !showGeofences) {
                map.removeGeofences();
            } else if (geofences && showGeofences) {
                drawGeofences();
            }
        }
    }, [map, showGeofences, geofences]);

    return {
        devices,
        showGeofences,
        setShowGeofences,
        createGeofenceForCurrentDevice,
        focusEventGeofence,
        clearGeofenceFocus,
    };
};

export default useMapGeofences;
